/* eslint-disable react/no-danger */
import React from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconSuccess, IconEditSmall } from '@sololearnorg/sol';
import { Container, AuthService } from '../../../../symphony';

import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlMessageDrawer } from '../../../../shared/public/SlMessageDrawer/SlMessageDrawer';
import { Meta } from '../Meta/Meta';
import { Voting } from '../Voting/Voting';
import { SlDiscussContext } from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlDataService } from '../../services/sl-data.service';
import {
	ActionType,
	RequestType,
	LinkClickSource,
} from '../../global-interface';

import s from './Reply.scss';
import { IDiscussReply } from '../../../../api/public/discuss-api';

interface IReplyProps {
	replyData: IDiscussReply;
}

export function Reply({ replyData }: IReplyProps) {
	useStyles(s);
	const authService = Container.take('global', AuthService);
	const isLoggedIn = authService.isLoggedIn();
	const { actions$ } = Container.take(SlDiscussContext, SlActions);
	const { isMyQuestion$: isMyQuestion } = useContainerData(
		SlDiscussContext,
		SlDataService,
		['isMyQuestion$'],
	);
	const isMyReply = replyData.userID === authService.userInfo$.value?.id;

	const bestAnswerButtonClickHandler = () => {
		actions$.next({
			type: ActionType.OnBestAnswerButtonClick,
			requestType: RequestType.Parallel,
			data: {
				replyId: replyData.id,
			},
		});
	};

	const handleJumpLinkClick = (
		linkClickSource: LinkClickSource,
		text: string,
		ctaURL: string,
	): void => {
		actions$.next({
			type: ActionType.OnJumpLinkClick,
			requestType: RequestType.Parallel,
			data: {
				linkClickSource,
				text,
				ctaURL,
			},
		});
	};

	const handleEditClick = () => {
		actions$.next({
			type: ActionType.OnReplyEditButtonClick,
			requestType: RequestType.Parallel,
			data: {
				id: replyData.id,
				text: replyData.message,
			},
		});
	};

	return (
		<div
			className={`discuss-reply${replyData.isAccepted ? ' discuss-reply__active' : ''
			}`}
		>
			{isMyReply && (
				<IconEditSmall
					className="discuss-reply__edit-button"
					onClick={handleEditClick}
				/>
			)}
			<div className="discuss-reply__container">
				<div className="discuss-reply__actions">
					{(isMyQuestion || replyData.isAccepted) && (
						<IconSuccess
							className={`discuss-reply__actions__best-answer-button${replyData.isAccepted
								? ' discuss-reply__actions__best-answer-button__active'
								: ''
							}${isMyQuestion
								? ' discuss-reply__actions__best-answer-button__clickable'
								: ''
							}`}
							onClick={bestAnswerButtonClickHandler}
						/>
					)}
					<Voting
						postId={replyData.id}
						userVoteCount={replyData.vote}
						voteCount={replyData.votes}
					/>
				</div>

				{!!replyData.message && (
					<div className="discuss-reply__content">
						<SlMessageDrawer
							message={replyData.message}
							options={{
								enableJumpLink: !isLoggedIn,
								enableLinkMessage: true,
								enableUserMention: true,
							}}
							callbacks={{
								handleJumpLinkClick: (text, link) => handleJumpLinkClick(
									LinkClickSource.ANSWER,
									text,
									link,
								),
							}}
						/>
					</div>
				)}
			</div>
			<Meta
				avatarUrl={replyData.avatarUrl}
				date={replyData.date}
				userID={replyData.userID}
				userName={replyData.userName}
				badge={replyData.badge}
			/>
		</div>
	);
}
